import { useState, useEffect, useCallback } from "react";
import PropTypes from "prop-types";
import { Box } from "@mui/material";
import { useSnackbar } from "notistack";
import { backendPost } from "@accredo/fe-shared/services/dataService.js";
import Button from "@accredo/fe-shared/theme/components/Button.jsx";
import {
  ContentDialog,
  ContentDialogInit,
} from "../MDDForm/components/ContentDialog.jsx";
import ClaimCostsEditor, {
  prepareForSubmission,
} from "../claim/costs/ClaimCostsEditor.jsx";
import { useSessionFiles } from "../Form/controls/FilePicker/useSessionFiles.js";
import {
  useClaimAttachmentFiles,
  uploadClaimAttachment,
} from "../Form/controls/FilePicker/useClaimAttachmentFiles.js";

const ClaimCostsModal = ({
  claimId,
  costs = [],
  coverageType = null,
  costTypes = null,
  open,
  onClose,
  onSuccess,
}) => {
  const { enqueueSnackbar } = useSnackbar();

  const [editingCosts, setEditingCosts] = useState(costs);
  const [isSaving, setIsSaving] = useState(false);
  const [isEditorFormOpen, setIsEditorFormOpen] = useState(false);

  const {
    sessionFiles: sessionAttachmentFiles,
    addSessionFile: addSessionAttachmentFile,
    updateSessionFileDescription: updateSessionAttachmentFileDescription,
    getUuidForServerId,
    clearSessionFiles: clearSessionAttachments,
  } = useSessionFiles();

  const {
    files: claimAttachmentFiles,
    isLoading: claimAttachmentsLoading,
    refetch: refetchClaimAttachments,
  } = useClaimAttachmentFiles({
    claimId,
  });

  // Bind upload to the current claimId (null → orphan attachment for new claims).
  const uploadFn = useCallback(
    (file, uuid, description) =>
      uploadClaimAttachment(claimId, file, uuid, description),
    [claimId]
  );

  // Reset editing costs when modal opens with new costs. On a coverage-form
  // claim the modal edits the rows of the claim's Sparte only.
  useEffect(() => {
    if (open) {
      setEditingCosts(
        costs.filter((cost) => (cost.coverage_type ?? null) === coverageType)
      );
    }
  }, [open, costs, coverageType]);

  // ContentDialog state
  const cdState = ContentDialogInit();
  cdState.openCD = open;
  cdState.setOpenCD = (value) => {
    if (!value) {
      onClose();
    }
  };

  const handleCostsChange = (updatedCosts) => {
    setEditingCosts(updatedCosts);
  };

  const handleEditorFormOpenChange = (isFormOpen) => {
    setIsEditorFormOpen(isFormOpen);
  };

  const handleSave = async () => {
    setIsSaving(true);

    try {
      const resolvedCosts = prepareForSubmission(
        editingCosts,
        getUuidForServerId
      );

      // Use sync endpoint - backend handles create/update/delete logic
      // and protects locked costs from modification
      const result = await backendPost(`data/schadens/${claimId}/costs/sync`, {
        label: "Kostenpositionen speichern",
        toSend: { costs: resolvedCosts, coverage_type: coverageType },
        enqueueSnackbar,
      });

      enqueueSnackbar("Kostenpositionen erfolgreich gespeichert.", {
        variant: "success",
      });

      if (result?.sync_stats?.locked_skipped > 0) {
        enqueueSnackbar(
          "Bereits gebuchte Kostenpositionen können nicht mehr geändert werden. Diese Änderungen wurden nicht übernommen.",
          { variant: "warning" }
        );
      }

      // Notify parent to refresh data
      if (onSuccess) {
        onSuccess();
      }
      onClose();
    } catch (error) {
      console.error("Error saving costs:", error);
      enqueueSnackbar("Fehler beim Speichern der Kostenpositionen.", {
        variant: "error",
      });
    } finally {
      setIsSaving(false);
    }
  };

  const handleClose = () => {
    onClose();
  };

  // Define tca (title, content, actions) for ContentDialog
  cdState.tca = {
    title: "Kostenpositionen bearbeiten",
    content: (
      <ClaimCostsEditor
        value={editingCosts}
        onChange={handleCostsChange}
        onFormOpenChange={handleEditorFormOpenChange}
        disabled={isSaving}
        claimId={claimId}
        claimAttachmentFiles={claimAttachmentFiles}
        sessionAttachmentFiles={sessionAttachmentFiles}
        addSessionAttachmentFile={(file) =>
          addSessionAttachmentFile(file, uploadFn)
        }
        updateSessionAttachmentFileDescription={
          updateSessionAttachmentFileDescription
        }
        getUuidForServerId={getUuidForServerId}
        clearSessionAttachments={clearSessionAttachments}
        costTypes={costTypes}
      />
    ),
    actions: (
      <Box sx={{ display: "flex", gap: 2 }}>
        <Button
          variant="tertiary"
          onClick={handleClose}
          disabled={isSaving || isEditorFormOpen}
        >
          Abbrechen
        </Button>
        <Button
          onClick={handleSave}
          disabled={isSaving || isEditorFormOpen}
          loading={isSaving}
        >
          Speichern
        </Button>
      </Box>
    ),
  };

  return (
    <ContentDialog
      openCD={open}
      setOpenCD={(value) => {
        if (!value) {
          onClose();
        }
      }}
      tca={cdState.tca}
      maxWidth="md"
      onClose={handleClose}
      disableBackdropClick={true}
    />
  );
};

ClaimCostsModal.propTypes = {
  claimId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  costs: PropTypes.array,
  coverageType: PropTypes.string,
  costTypes: PropTypes.arrayOf(PropTypes.string),
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onSuccess: PropTypes.func,
};

export default ClaimCostsModal;
